import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { detectConfig, useConfig } from "providers/ConfigProvider";
import fetchExclusive from "utils/fetchExclusive";

const { apiUrl } = detectConfig();

async function fetchData(path, options, simple = false) {
  const res = await fetchExclusive(`${apiUrl}${path}`, {
    method: "GET",
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
  if (simple) {
    return res;
  }
  const data = await res.json();
  if (data.error) {
    throw new Error(data.error.message);
  }
  return data;
}

export const AuthContext = createContext(null);

export function useAuth() {
  if (!AuthContext) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return useContext(AuthContext);
}

export function Disconnect() {
  const auth = useAuth();
  useEffect(() => {
    auth.logout();
  }, [auth]);
  return <></>;
}

export function AuthGuard({
  children,
  connected,
  redirectTo,
  forceLogout = false,
}) {
  const auth = useAuth();
  const navigate = useNavigate();

  const passes = (auth.user && connected) || (!auth.user && !connected);
  const shouldBeDisconnected = auth.user && !connected && forceLogout;

  useEffect(() => {
    if (auth.isLoading) return;
    if (shouldBeDisconnected) {
      auth.logout(false);
    } else if (!passes && redirectTo) {
      navigate(redirectTo);
    }
  }, [passes, redirectTo, navigate, shouldBeDisconnected, auth]);

  if (auth.isLoading) return <></>;
  return passes && !shouldBeDisconnected ? children : <></>;
}

AuthGuard.propTypes = {
  children: PropTypes.any.isRequired,
  connected: PropTypes.bool.isRequired,
  redirectTo: PropTypes.string,
  forceLogout: PropTypes.bool,
};

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPending, setIsPending] = useState(false);

  const {
    isCustomerUrl,
    isPartnerUrl,
    isManagerUrl,
    customerUrl,
    managerUrl,
    partnerUrl,
  } = useConfig();
  const navigate = useNavigate();

  const role = user?.role?.name || "";
  const isCustomer = isCustomerUrl && (!role || role === "Client");
  const isPartner = isPartnerUrl && role === "Partenaire";
  const isAdmin = isManagerUrl && role === "Administrateur";
  const isManager =
    (!isCustomerUrl && !isPartnerUrl && role === "Administrateur") ||
    role === "Gestionnaire" ||
    role === "Consultation";

  // Redirige vers le bon espace selon le rôle. Retourne true si une redirection a eu lieu.
  const redirectToCorrectSpace = useCallback(
    (userData) => {
      const roleName = userData.role.name;
      const isCustomerRole = roleName === "Client";
      const isPartnerRole = roleName === "Partenaire";
      const isManagerRole = roleName && !isCustomerRole && !isPartnerRole;

      if (isCustomerRole && !isCustomerUrl) {
        window.location.replace(customerUrl);
        return true;
      }
      if (isPartnerRole && !isPartnerUrl) {
        window.location.replace(partnerUrl);
        return true;
      }
      if (isManagerRole && !isManagerUrl) {
        window.location.replace(managerUrl);
        return true;
      }
      return false;
    },
    [
      isCustomerUrl,
      isPartnerUrl,
      isManagerUrl,
      customerUrl,
      partnerUrl,
      managerUrl,
    ],
  );

  // Restauration de la session au chargement via le cookie httpOnly
  useEffect(() => {
    fetchData("/api/users/me?populate=role", {}, true)
      .then(async (res) => {
        if (res.ok) {
          const userData = await res.json();
          if (!redirectToCorrectSpace(userData)) {
            setUser(userData);
          }
        }
        const publicPaths = [
          "/",
          "/login",
          "/reset-password",
          "/forgot-password",
          "/create-account",
          "/email-confirmation",
        ];
        if (
          res.status === 401 &&
          !publicPaths.includes(window.location.pathname)
        ) {
          navigate("/login?error=expired");
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [navigate, redirectToCorrectSpace]);

  // Vérifie la validité de la session lorsque l'onglet reprend le focus
  const visibilityChange = useCallback(() => {
    if (window.location.pathname !== "/login" && user) {
      fetchData("/api/users/me?populate=role", {}, true)
        .then(async (res) => {
          if (res.status === 401) {
            setUser(null);
            navigate("/login?error=expired");
          } else {
            const userData = await res.json();
            redirectToCorrectSpace(userData);
          }
        })
        .catch(() => {});
    }
  }, [user, navigate, redirectToCorrectSpace]);

  useEffect(() => {
    document.addEventListener("visibilitychange", visibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", visibilityChange);
    };
  }, [visibilityChange]);

  const login = async (
    identifier,
    password,
    { simulationId, simulationCode } = {},
  ) => {
    setIsPending(true);
    try {
      await fetchData("/api/auth/local", {
        method: "POST",
        body: JSON.stringify({
          identifier,
          password,
          simulationId,
          simulationCode,
        }),
      });

      const userData = await fetchData("/api/users/me?populate=role");

      if (!redirectToCorrectSpace(userData)) {
        setUser(userData);
        navigate("/");
      }
    } finally {
      setIsPending(false);
    }
  };

  const logout = async (redirect = true) => {
    try {
      await fetchData("/api/auth/logout", { method: "POST" });
    } catch (e) {
      // Le cookie expirera naturellement
    }
    setUser(null);
    if (redirect && window.location.pathname !== "/login") {
      navigate("/login");
    }
  };

  const deleteUser = async () => {
    try {
      setIsPending(true);
      const data = await fetchData(`/api/client/${user.id}`, {
        method: "DELETE",
      });
      if (data.success === true) logout();
    } catch (error) {
      console.warn(error);
    } finally {
      setIsPending(false);
    }
  };

  const value = {
    user,
    role,
    isCustomer,
    isManager,
    isPartner,
    isAdmin,
    isLoading,
    isPending,
    login,
    logout,
    deleteUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.any.isRequired,
};
